/**
 * RedirectHelper
 *
 * All "redirect out of AskMe" logic for the chatbotexpand component.
 *
 * Public entry points (bound from chatbotexpand.component.html):
 *  - redirectToModule   -> opens /Crosstab or /BrandAnalysis in a new tab
 *  - redirectToSnapshot -> opens /Snapshot/custom-snapshot-output in a new tab
 *
 * Both hand off state to the target module through localStorage. The remaining
 * methods are the AskMe -> leftpanel/output scenario translation used by
 * redirectToModule and are internal to this helper.
 *
 * Extracted verbatim from chatbotexpand.component.ts (`this.` -> `comp.`);
 * behavior is unchanged.
 */
export class RedirectHelper {

  static redirectToModule(comp: any, block: any, moduleName: 'Crosstab' | 'BrandAnalysis'): void {
    const analytical = block?.analyticalData || {};
    let payload: any;
    let originalPipes: any = null;
    if (moduleName === 'Crosstab') {
      payload = block?.crosstabPayload
        || analytical?.getConfiguration?.crosstab_payload
        || analytical?.AgentPayload?.getConfiguration?.crosstab_payload
        || comp.lastCrosstabPayload;
    } else {
      payload = block?.brandAnalysisPayload
        || analytical?.getConfiguration?.brand_analysis_payload
        || analytical?.getConfiguration?.brand_anlysis_payload
        || comp.lastBrandAnalysisPayload;
      // For Brand Analysis: prefer original pipe strings (chatPayload.row/column) for Level0/Level1 derivation
      originalPipes = block?.brandAnalysisOriginalPipes || comp.lastBrandAnalysisOriginalPipes || null;
    }

    if (!payload) {
      return;
    }

    const scenarioData = RedirectHelper.buildAskMeScenarioObject(comp, payload, originalPipes);

    const redirectData = {
      scenarioData,
      landingdata: {
        client_code: comp.selected_client,
        study: comp.selected_study,
        studyList: comp.landingdata?.studyList || []
      }
    };

    const storageKey = moduleName === 'Crosstab' ? 'askme_crosstab_redirect' : 'askme_brandanalysis_redirect';
    localStorage.setItem(storageKey, JSON.stringify(redirectData));
    window.open(`/${moduleName}`, '_blank');
  }

  static redirectToSnapshot(comp: any, block: any): void {
    // Use chatPayload as primary source — it always has the correct flat keys used for the API call
    const chatP = comp.chatPayload || {};
    const opData = block?.snapshotPayload || comp.lastSnapshotPayload || {};
    const widgetConfig = opData?.response_payload?.widegtconfigdetails
      || opData?.response_payload_trendline?.widegtconfigdetails
      || {};

    const snapshotName = chatP.Snapshot_Name || chatP.snapshot_name
      || widgetConfig.snapshotName || opData.Snapshot_Name || opData.snapshot_name
      || comp.selected_study || '';
    const snapshotType = chatP.snapshot_type || chatP.Snapshot_Type
      || widgetConfig.snapshotType || opData.snapshot_type || '';
    const section = chatP.Section || chatP.section
      || widgetConfig.section || opData.Section || '';
    const subsection = chatP.SubSection || chatP.sub_section
      || widgetConfig.subSection || opData.SubSection || '';
    const mainStudy = chatP.study || widgetConfig.study || comp.selected_study || '';
    const clientCode = chatP.client_name || comp.selected_client || '';
    const page = opData?.response_payload?.page || 1;

    if (!snapshotName) { console.warn('[redirectToSnapshot] snapshotName could not be resolved', { chatP, opData }); return; }

    console.log('[redirectToSnapshot] redirecting with', { snapshotName, snapshotType, section, mainStudy, clientCode, page });
    console.log('[redirectToSnapshot] full chatP=', JSON.stringify(chatP));

    const redirectData = {
      client_code: clientCode,
      study: snapshotName,
      main_study: mainStudy,
      snapshotselected: snapshotType,
      sectionselected: section,
      subsectionselected: subsection,
      page,
      dataModel: opData?.response_payload?.dataModel || opData?.response_payload_trendline?.dataModel || null,
      entryPointData: chatP.entryPointData || opData?.response_payload?.dataModel?.entryPointData || null,
      widget_name: chatP.Widget_Name || chatP.widget_name || opData?.Widget_Name || opData?.widget_name || '',
      widget_id: chatP.Widget_ID || chatP.widget_id || opData?.Widget_ID || opData?.widget_id || '',
      primary_brands: chatP.primary_brands || null,
      timeperiod: chatP.timeperiod || null,
      market: chatP.market || null,
      filter_audience: chatP.Filter_Audience || chatP.filter_audience || null,
      metric_filters: chatP.Metric_Filters || chatP.metric_filters || null
    };
    console.log('[redirectToSnapshot] filter_audience=', redirectData.filter_audience, 'metric_filters=', redirectData.metric_filters);

    localStorage.setItem('askme_snapshot_redirect', JSON.stringify(redirectData));
    localStorage.setItem('snapshotselected_landing', snapshotType);
    localStorage.setItem('sectionselected_landing', section);
    localStorage.setItem('subsectionselected_landing', subsection);
    window.open('/Snapshot/custom-snapshot-output', '_blank');
  }

  private static normalizeAskMeBlock(comp: any, block: any, isBrandAnalysis: boolean = false): any {
    if (isBrandAnalysis) return block;
    const normalizedData = (block.Data || []).map((d: any) => {
      if (d.isTimePeriod) return d;
      if (d.isMarket) {
        // "Markets|Market|Market 01" (3-part) → "Markets|Market 01" (2-part)
        const parts = (d.Child || '').split('|').filter((p: string) => p.trim());
        if (parts.length === 3 && parts[0].toLowerCase() === 'markets') {
          return { ...d, Child: `${parts[0]}|${parts[2]}` };
        }
        return d;
      }
      const parts = (d.Child || '').split('|').filter((p: string) => p.trim());
      // Parent "UK" and child: "Country|UK" --> which is wrong, should be parent "Country" and child "Country|UK". So we need to fix it here.
      // Some clients' mandatory entry-point fields (e.g. ASICS' Country) arrive with
      // isMarket:false. Detected structurally: if the raw incoming block.Parent already
      // matches the category half of Child (parts[0]) rather than the value/metric half
      // (parts[1]), this is a mandatory-field-style item, not an ordinary "Level0|Level1"
      // chip (where Parent normally matches parts[1] — see comment below). Flagging it
      // isMarket:true routes it through the Parent-preserving branch instead of the
      // generic branch, which would otherwise overwrite Parent with parts[1] (the
      // selected value, e.g. "UK") instead of the category ("Country").
      const looksLikeMandatoryField = parts.length === 2 && block.Parent === parts[0] && block.Parent !== parts[1];
      if (looksLikeMandatoryField) {
        return { ...d, isMarket: true };
      }

      if (parts.length <= 2) return d;
      // Preserve original Level0 so buildAskMeListFromZone can match variableTabData
      return { ...d, Child: parts.slice(1).join('|'), _originalLevel0: parts[0] };
    });

    let newParent = block.Parent;
    if (normalizedData.length > 0 && normalizedData[0].isMarket) {
      // Market/mandatory-type items: derive Parent from Child's category segment
      // (e.g. "Country" from "Country|UK") rather than letting the generic branch
      // below overwrite it with the selected value.
      const parts = (normalizedData[0].Child || '').split('|').filter((p: string) => p.trim());
      newParent = parts[0] || block.Parent;
    } else if (normalizedData.length > 0 && !normalizedData[0].isTimePeriod) {
      const firstChild = normalizedData[0].Child || '';
      const parts = firstChild.split('|').filter((p: string) => p.trim());
      if (parts.length >= 1) {
        // Brand column variables (brandanalysisCode 7): keep parts[0] so all brand Data items
        // share Parent="Brands" for grouping. Normalized paths (_originalLevel0 set): parts[0]
        // is already Level1. For regular 2-part "Level0|Level1" paths: use parts[1] so
        // dataObject.Parent matches chip.Level1, enabling updateSelectAllMetricStatus to find
        // the correct entry and restore selection state.
        if (normalizedData[0]._originalLevel0 || block.brandanalysisCode === 7 || parts.length < 2) {
          newParent = parts[0];
        } else {
          newParent = parts[1];
        }
      }
    }

    return { ...block, Parent: newParent, Data: normalizedData };
  }

  private static splitBlocksByLevel1(comp: any, blocks: any[]): any[] {
    const result: any[] = [];
    blocks.forEach((block: any) => {
      const byLevel1 = new Map<string, any[]>();
      (block.Data || []).forEach((d: any) => {
        const parts = (d.Child || '').split('|').filter((p: string) => p.trim());
        const level1 = parts[0] || block.Parent || '';
        if (!byLevel1.has(level1)) byLevel1.set(level1, []);
        byLevel1.get(level1)!.push(d);
      });
      if (byLevel1.size <= 1) {
        result.push(block);
      } else {
        byLevel1.forEach((items, level1) => {
          result.push({ ...block, Parent: level1, Data: items });
        });
      }
    });
    return result;
  }

  private static buildAskMeScenarioObject(comp: any, payload: any, originalPipes?: any): any {
    const isTotalBase = payload.isTotalBase || false;
    const significance = payload.significance || 'None';
    const entryPointData = payload.entryPointData || [];

    // Empty pipes (e.g. restored from history) must not count as usable, or Row/Column end up empty.
    const hasUsablePipes = !!originalPipes && ((originalPipes.row?.length || 0) > 0 || (originalPipes.column?.length || 0) > 0);

    const normalizeBlocks = (blocks: any[]) => blocks.map((b: any) => RedirectHelper.normalizeAskMeBlock(comp, b, hasUsablePipes));

    const normRow = RedirectHelper.splitBlocksByLevel1(comp, normalizeBlocks(payload.Row || []));
    const normRowNesting = RedirectHelper.splitBlocksByLevel1(comp, normalizeBlocks(payload.RowNesting || []));
    const normColumn = RedirectHelper.splitBlocksByLevel1(comp, normalizeBlocks(payload.Column || []));
    const normColNesting = RedirectHelper.splitBlocksByLevel1(comp, normalizeBlocks(payload.ColNesting || []));
    const normFilter = normalizeBlocks(payload.Filter || []);

    // Brand Analysis uses chatPayload pipe strings for correct Level0/Level1; Crosstab (no pipes) uses the zone builder.
    let listA: any[], listB: any[], listC: any[], listD: any[];
    if (hasUsablePipes) {
      listA = RedirectHelper.buildAskMeListFromBAPipes(originalPipes.row || [], payload.Row || [], entryPointData);
      listB = RedirectHelper.buildAskMeListFromBAPipes(originalPipes.row_nesting || [], payload.RowNesting || [], entryPointData);
      listC = RedirectHelper.buildAskMeListFromBAPipes(originalPipes.column || [], payload.Column || [], entryPointData);
      listD = RedirectHelper.buildAskMeListFromBAPipes(originalPipes.column_nesting || [], payload.ColNesting || [], entryPointData);

      // Drop empty pipe-derived items so the fallback rebuilds auto-included/mismatched panels from the payload.
      listA = listA.filter((i: any) => i.data_response_code);
      listB = listB.filter((i: any) => i.data_response_code);
      listC = listC.filter((i: any) => i.data_response_code);
      listD = listD.filter((i: any) => i.data_response_code);
      listA.push(...RedirectHelper.buildFallbackListFromPayloadBlocks(payload.Row || [], listA));
      listB.push(...RedirectHelper.buildFallbackListFromPayloadBlocks(payload.RowNesting || [], listB));
      listC.push(...RedirectHelper.buildFallbackListFromPayloadBlocks(payload.Column || [], listC));
      listD.push(...RedirectHelper.buildFallbackListFromPayloadBlocks(payload.ColNesting || [], listD));
    } else {
      listA = RedirectHelper.buildAskMeListFromZone(comp, normRow, entryPointData);
      listB = RedirectHelper.buildAskMeListFromZone(comp, normRowNesting, entryPointData);
      listC = RedirectHelper.buildAskMeListFromZone(comp, normColumn, entryPointData);
      listD = RedirectHelper.buildAskMeListFromZone(comp, normColNesting, entryPointData);
    }
    const listE = RedirectHelper.buildAskMeFilterList(comp, normFilter, entryPointData, hasUsablePipes, originalPipes?.filters);


    const isTimePeriodPresent = normColumn.some((col: any) =>
      (col.Data || []).some((d: any) => d.isTimePeriod)
    );

    const dataObject = {
      Row: normRow,
      Column: normColumn,
      RowNesting: normRowNesting,
      ColNesting: normColNesting,
      Filter: normFilter,
      CustomGroup: []
    };

    const output_selection_data = {
      dataObject,
      confidenceLevel: payload.confidenceLevel || 'None',
      DefaultIndex: 'None',
      significance,
      sampleSizeBlock: false,
      weightedLable: false,
      DataLabel: true,
      Transpose: false,
      isTableView: true,
      isSig: true,
      isIndex: false,
      selectedIndex: '',
      isTimePeriodPresent,
      disableLineChart: false,
      isLineChartClick: false,
      disableStackChart: false,
      isVerticalStackChartClick: false,
      isVerticalGroupBarChartClick: false,
      isVerticalBarChartClick: false,
      isPairWise: significance === 'Pairwise',
      is_chart_visualization: true,
      isChartView: false,
      isTotal: false,
      DefaultDataDropDown: 'Data',
      isWeightedNumerator: false,
      isUnWeightedNumerator: false,
      isReference: false,
      isDisableReference: false,
      SignificanceVsReference: payload.SignificanceVsReference || '',
      is_indexing: undefined
    };

    let weightCode = payload.SelectedWeight || payload.weight || '';
    let weightName = '';

    if (weightCode) {
      if (comp?.WeightDropDownList && comp.WeightDropDownList.length > 0) {
        const matchedWeight = comp.WeightDropDownList.find((w: any) =>
          w.responseCode === weightCode ||
          w.Label === weightCode ||
          w.tooltip === weightCode
        );
        if (matchedWeight) {
          weightCode = matchedWeight.responseCode;
          weightName = matchedWeight.Label;
        }
      }

      if (!weightName) {
        // Priority 1: ES Index (variableTabData)
        const variableTabData = comp?.variableTabData || comp?.landingdata?.variableTabData || [];
        const vData = variableTabData.find((v: any) => v.data_variable_code === weightCode || v.Level1 === weightCode);
        if (vData && vData.Level1) {
          weightCode = vData.data_variable_code;
          weightName = vData.Level1;
        }
      }

      if (!weightName) {
        // Priority 2: Config payload fallback
        const configWeights = comp?.configWeights || comp?.landingdata?.configWeights || [];
        const foundConfig = configWeights.find((w: any) => w.variable_code === weightCode || w.variable_label === weightCode);
        if (foundConfig && foundConfig.variable_label) {
          weightCode = foundConfig.variable_code;
          weightName = foundConfig.variable_label.trim();
        }
      }
    }

    if (!weightName) {
      weightName = comp?.weightSelectedValue || payload.weightLabel || weightCode;
    }

    const leftpanel_input_data = {
      listA,
      listB,
      listC,
      listD,
      listE,
      isAnswerBase: !isTotalBase,
      isTotalBase,
      isFiltering: payload.isFiltering !== false,
      isProfiling: false,
      selectedWeight: weightCode,
      DefaultWeight: weightName || weightCode
    };

    return {
      selection_summary: '',
      leftpanel_input_data: JSON.stringify(leftpanel_input_data),
      output_selection_data: JSON.stringify(output_selection_data),
      entry_point_selection_data: JSON.stringify(entryPointData),
      isTotalBase,
      customBaseMetric: payload.customBaseMetric ? JSON.stringify(payload.customBaseMetric) : null
    };
  }

  // Builds left-panel items from chatPayload pipe strings (Level0|Level1|...|MetricOption), which carry
  // the correct hierarchy unlike brand_analysis_payload Data[].Child (Level1|MetricOption only).
  private static buildAskMeListFromBAPipes(
    pipes: string[],
    payloadBlocks: any[],
    entryPointData: any[]
  ): any[] {
    const groups = new Map<string, any>();

    (pipes || []).forEach((pipe: string) => {
      const parts = pipe.split('|').map((p: string) => p.trim()).filter((p: string) => p);
      if (parts.length < 1) return;

      const level0 = parts[0] || '';
      const level1 = parts.length >= 2 ? parts[1] : parts[0];
      const key = `${level0}|||${level1}`;

      if (!groups.has(key)) {
        // Find the matching payload block by Parent === level1
        const matchingBlock = payloadBlocks.find((b: any) => b.Parent === level1);
        const blockData = matchingBlock?.Data || [];
        const firstData = blockData[0];

        groups.set(key, {
          Level0: level0,
          Level1: level1,
          has_nextLevel: true,
          sortId: matchingBlock?.SortId || firstData?.sort_id || 0,
          is_Derived: firstData?.isDerviedVariable ? 'true' : 'false',
          is_calc_type: firstData?.CalculationType || '',
          data_variable_code: firstData?.variablecode || '',
          // Accumulate codes from ALL options under this parent, or the panel shows nothing selected.
          responseCodes: blockData.map((d: any) => d.responsecode).filter((c: any) => c !== undefined && c !== null),
          sortIds: blockData.map((d: any) => String(d.sort_id)),
          firstDate: firstData?.Date || '0001-01-01T00:00:00',
          yearCodes: blockData.map((d: any) => d.year_response_code || '').filter((c: string) => c),
          data_is_multi_punch: String(firstData?.isMultiPunch || 0),
          is_dimension: false,
          is_custmGrpPresent: false,
          kibanaFilterScript: firstData?.kibanaFilterScript || '',
          customGroupId: firstData?.customGroupId || '',
          dataYearCode: firstData?.year_variable_code || '',
          is_brand_analysis: matchingBlock?.brandanalysisCode || 0,
          is_entrypoint: matchingBlock?.is_entrypoint || false
        });
      }
    });

    const items: any[] = [];
    groups.forEach(grp => {
      items.push({
        Level0: grp.Level0,
        Level1: grp.Level1,
        has_nextLevel: grp.has_nextLevel,
        sortId: grp.sortId,
        is_Derived: grp.is_Derived,
        is_calc_type: grp.is_calc_type,
        tooltip: `${grp.Level1} | ${grp.data_variable_code}`,
        data_variable_code: grp.data_variable_code,
        data_response_code: grp.responseCodes.join(','),
        data_sort_id: grp.sortIds.join(','),
        data_is_multi_punch: grp.data_is_multi_punch,
        data_date: grp.firstDate,
        data_year_code: grp.yearCodes.join(','),
        is_dimension: grp.is_dimension,
        is_custmGrpPresent: grp.is_custmGrpPresent,
        kibanaFilterScript: grp.kibanaFilterScript,
        customGroupId: grp.customGroupId,
        dataYearCode: grp.dataYearCode,
        isSelectAllMetrics: false,
        isSelectAllMetricsCount: 0,
        is_brand_analysis: grp.is_brand_analysis,
        is_entrypoint: grp.is_entrypoint
      });
    });
    return items;
  }

  // Backfills payload panels (e.g. auto-included/NET metrics) not covered by any matched pipe, so they're never left empty.
  private static buildFallbackListFromPayloadBlocks(payloadBlocks: any[], alreadyCovered: any[]): any[] {
    const coveredParents = new Set(alreadyCovered.map((i: any) => i.Level1));
    const items: any[] = [];
    payloadBlocks.forEach((block: any) => {
      const parent = block?.Parent;
      const blockData = block?.Data || [];
      if (!parent || coveredParents.has(parent) || blockData.length === 0) return;
      coveredParents.add(parent);
      const firstData = blockData[0];
      items.push({
        Level0: parent,
        Level1: parent,
        has_nextLevel: true,
        sortId: block.SortId || firstData?.sort_id || 0,
        is_Derived: firstData?.isDerviedVariable ? 'true' : 'false',
        is_calc_type: firstData?.CalculationType || '',
        tooltip: `${parent} | ${firstData?.variablecode || ''}`,
        data_variable_code: firstData?.variablecode || '',
        data_response_code: blockData.map((d: any) => d.responsecode).filter((c: any) => c !== undefined && c !== null).join(','),
        data_sort_id: blockData.map((d: any) => String(d.sort_id)).join(','),
        data_is_multi_punch: String(firstData?.isMultiPunch || 0),
        data_date: firstData?.Date || '0001-01-01T00:00:00',
        data_year_code: blockData.map((d: any) => d.year_response_code || '').filter((c: string) => c).join(','),
        is_dimension: false,
        is_custmGrpPresent: false,
        kibanaFilterScript: firstData?.kibanaFilterScript || '',
        customGroupId: firstData?.customGroupId || '',
        dataYearCode: firstData?.year_variable_code || '',
        isSelectAllMetrics: false,
        isSelectAllMetricsCount: 0,
        is_brand_analysis: block.brandanalysisCode || 0,
        is_entrypoint: block.is_entrypoint || false
      });
    });
    return items;
  }

  private static buildAskMeListFromZone(comp: any, zone: any[], entryPointData: any[]): any[] {
    const groups = new Map<string, any>();

    zone.forEach((parent: any) => {
      (parent.Data || []).forEach((d: any) => {
        const parts = (d.Child || '').split('|').filter((p: string) => p.trim());
        // For 2-part "Level0|Level1" paths without normalization, parts[0] is Level0 (category)
        // and parts[1] is Level1 (the actual metric name shown on the chip).
        // Exceptions: normalized paths (_originalLevel0 set), market/time items, and brand
        // column variables (brandanalysisCode 7) which collapse all brands into one chip.
        const level1: string = (d._originalLevel0 || d.isMarket || d.isTimePeriod || parent.brandanalysisCode === 7)
          ? (parts[0] || parent.Parent || '')
          : (parts.length >= 2 ? parts[1] : (parts[0] || parent.Parent || ''));
        let level0: string;

        if (d.isTimePeriod) {
          const ep = entryPointData[0];
          const mapping = (ep?.mandatory_variable_mapping || []).find(
            (m: any) => m.variable_code === d.variablecode
          );
          level0 = mapping?.variable_label || 'Time Period';
        } else if (d.isMarket) {
          level0 = parts[0] || parent.Parent || '';
        } else {
          level0 = d._originalLevel0 || (parts.length >= 2 ? parts[0] : level1);
        }

        // Normalized paths share the same level1 category across many variablecodes; exclude variablecode to produce one chip per category.
        const key = d._originalLevel0
          ? `${level0}|||${level1}`
          : `${level0}|||${level1}|||${d.variablecode}`;
        if (!groups.has(key)) {
          groups.set(key, {
            Level0: level0,
            Level1: level1,
            has_nextLevel: true,
            sortId: d.sort_id || 0,
            is_Derived: d.isDerviedVariable ? 'true' : 'false',
            is_calc_type: d.CalculationType || '',
            data_variable_code: d.variablecode || '',
            responseCodes: [],
            sortIds: [],
            firstDate: d.Date || '0001-01-01T00:00:00',
            yearCodes: [],
            data_is_multi_punch: String(d.isMultiPunch || 0),
            is_dimension: false,
            is_custmGrpPresent: false,
            kibanaFilterScript: d.kibanaFilterScript || '',
            customGroupId: d.customGroupId || '',
            dataYearCode: d.year_variable_code || '',
            isSelectAllMetrics: false,
            isSelectAllMetricsCount: 0,
            is_brand_analysis: parent.brandanalysisCode || 0,
            is_entrypoint: parent.is_entrypoint || false
          });
        }

        const grp = groups.get(key)!;
        if (!grp.responseCodes.includes(d.responsecode)) {
          grp.responseCodes.push(d.responsecode);
          grp.sortIds.push(String(d.sort_id));
          grp.yearCodes.push(d.year_response_code || '');
        }
      });
    });

    const items: any[] = [];
    groups.forEach(grp => {
      items.push({
        Level0: grp.Level0,
        Level1: grp.Level1,
        has_nextLevel: grp.has_nextLevel,
        sortId: grp.sortId,
        is_Derived: grp.is_Derived,
        is_calc_type: grp.is_calc_type,
        tooltip: `${grp.Level1} | ${grp.data_variable_code}`,
        data_variable_code: grp.data_variable_code,
        data_response_code: grp.responseCodes.join(','),
        data_sort_id: grp.sortIds.join(','),
        data_is_multi_punch: grp.data_is_multi_punch,
        data_date: grp.firstDate,
        data_year_code: grp.yearCodes.filter((c: string) => c).join(',') || '',
        is_dimension: grp.is_dimension,
        is_custmGrpPresent: grp.is_custmGrpPresent,
        kibanaFilterScript: grp.kibanaFilterScript,
        customGroupId: grp.customGroupId,
        dataYearCode: grp.dataYearCode,
        isSelectAllMetrics: false,
        isSelectAllMetricsCount: 0,
        is_brand_analysis: grp.is_brand_analysis,
        is_entrypoint: grp.is_entrypoint
      });
    });
    return items;
  }

  private static buildAskMeFilterList(comp: any, filter: any[], entryPointData: any[] = [], isBrandAnalysis = false, originalFilters: any = null): any[] {
    const seen = new Set<string>();
    const items: any[] = [];
    const filterPipes = Object.values(originalFilters || {}).flatMap((value: any) => Array.isArray(value) ? value : [value]);
    filter.forEach((parent: any, idx: number) => {
      const firstData = (parent.Data || [])[0];
      // For non-entry-point filters (e.g. Audience/Gender), use _originalLevel0 so the
      // expand dropdown can look up items from variableTabData by Level0+Level1.
      // For entry-point filters (Markets), _originalLevel0 is not set → falls back to Parent.
      // Time period filters (isTimePeriod=true) live under "Time Period" in variableTabData.
      let level0: string;
      let level1: string = parent.Parent;
      if (firstData?.isTimePeriod) {
        const ep = entryPointData[0];
        const mapping = (ep?.mandatory_variable_mapping || []).find(
          (m: any) => m.variable_code === firstData.variablecode
        );
        level0 = mapping?.variable_label || 'Time Period';
      } else {
        const childParts = String(firstData?.Child || '').split('|').filter((p: string) => p.trim());
        const matchingPipe = isBrandAnalysis && filterPipes.find((pipe: any) => {
          const parts = String(pipe).split('|').map((part: string) => part.trim()).filter(Boolean);
          return parts.length > 1 && (parts[1] === parent.Parent || parts[0] === parent.Parent);
        });
        const pipeParts = matchingPipe ? String(matchingPipe).split('|').map((part: string) => part.trim()).filter(Boolean) : [];
        level0 = firstData?._originalLevel0 || (pipeParts.length > 1 ? pipeParts[0] : '')
          || (isBrandAnalysis && !firstData?.isMarket && childParts.length > 1 ? childParts[0] : parent.Parent);
      }
      const key = `${level0}|||${level1}`;
      if (!seen.has(key)) {
        seen.add(key);
        items.push({
          Level0: level0,
          Level1: level1,
          tooltip: '',
          has_nextLevel: true,
          sortId: idx,
          is_Derived: 'false',
          is_calc_type: 'false',
          is_dimension: false,
          is_custmGrpPresent: false,
          dataYearCode: '',
          isSelectAllMetrics: false,
          isSelectAllMetricsCount: 0
        });
      }
    });
    return items;
  }
}
